import React from "react";
import { useSelector } from "react-redux";

import { useFormikContext } from "formik";

import { SubtleBadge } from "components/Badge";
import { CollapseLabel, FormCollapse } from "components/Form";
import { Row } from "components/Layout";

import { VendorCommissionInput } from "components/Form/FormikControls";
import {
  getActiveLivestockAgentDeployment,
  getSaleVendorCommissionByDeploymentId,
  getVendorCommissionForCurrentSaleByDeploymentId,
  getVendorCommissionForCurrentSaleByBusinessId,
} from "selectors";

import { EMPTY_OBJECT } from "lib";
import { commissionFormatter } from "lib/agGrid/formatters";
import {
  resolveEffectiveVendorCommission,
  getVendorCommissionSourceLabel,
} from "lib/vendorCommission";

import { useCanAddVendorCommissionPermission } from "hooks";

function VendorCommissionHeader({
  commissionDescription,
}: {
  commissionDescription: string;
}) {
  return (
    <Row>
      <SubtleBadge>Vendor Commission</SubtleBadge>
      <CollapseLabel>{commissionDescription}</CollapseLabel>
    </Row>
  );
}

interface VendorCommissionSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  readOnly: boolean;
}

export const VendorCommissionSection = ({
  isOpen,
  onToggle,
  readOnly,
}: VendorCommissionSectionProps) => {
  // TODO type consignment and form values properly, partial placeholder for now
  const formikProps: {
    values: { vendor_id: string | null; vendorCommission: Commission | null };
  } = useFormikContext();
  const { vendor_id: vendorId, vendorCommission: consignmentCommission } =
    formikProps.values;
  const { id: deploymentId } = useSelector(getActiveLivestockAgentDeployment);

  const canCreateVendorCommission = useCanAddVendorCommissionPermission();

  const deploymentCommission: VendorCommissionBand = useSelector(
    getVendorCommissionForCurrentSaleByDeploymentId(deploymentId),
  );
  const saleCommission: Commission = useSelector(
    getSaleVendorCommissionByDeploymentId(deploymentId),
  );
  const vendorCommission: VendorCommissionBand = useSelector(
    getVendorCommissionForCurrentSaleByBusinessId(vendorId),
  );

  const { commission, source } =
    resolveEffectiveVendorCommission({
      consignmentCommission,
      deploymentCommission,
      saleCommission,
      vendorCommission,
    }) || EMPTY_OBJECT;

  const disabled: boolean =
    readOnly || (!vendorCommission && !canCreateVendorCommission);

  const commissionDescription = commission
    ? `${getVendorCommissionSourceLabel(source)} @ ${commissionFormatter(commission)}`
    : "";

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={
        <VendorCommissionHeader commissionDescription={commissionDescription} />
      }
      dataTour={isOpen ? "hideVendorCommission" : "showVendorCommission"}
    >
      <VendorCommissionInput
        name="vendorCommission"
        addLabel="Set Vendor Commission"
        disabled={disabled}
      />
    </FormCollapse>
  );
};
